import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetSalesReportSalesLedger,
  GetSalesReportItemProfit,
  GetSalesReportCashFlow,
  GetSalesReportCheques,
  GetSalesReportDiscounts,
  GetSalesReportNonCashPayments,
  GetSalesReportEmployeePerformance,
  GetInventoryReportProductStock,
  GetInventoryReportExpiringItems,
  GetInventoryReportPurchases,
  GetInventoryReportPurchasedProducts,
  GetInventoryReportReturnedItems,
  GetInventoryReportSoldItems,
  GetCourierReport,
  GetSalesReportCashOuts,
  GetSignOffReport,
  GetSalesReportByCashier,
  GetInventoryReportTotalItems,
  GetInventoryReportProducts,
  GetPurchaseReportReturns,
  GetPurchaseReportPaymentHistory,
  GetExpenseReportBySellerCategory,
  GetCreditReportDebtors,
  GetCreditReportCreditors,
  GetCreditReportCustomers,
  GetCreditReportSuppliers,
  GetOtherReportRawStockIssue,
  GetOtherReportRawMaterialPurchases,
  GetOtherReportProductions,
  GetOtherReportTransfers,
  GetPayrollSummaryReport,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const reportQueryKeys = {
  payrollSummaryReport: (
    params?: Parameters<typeof GetPayrollSummaryReport>[0],
  ) => ["payrollSummaryReport", params] as const,
  salesReportSalesLedger: (
    params?: Parameters<typeof GetSalesReportSalesLedger>[0],
  ) => ["salesReportSalesLedger", params] as const,
  salesReportItemProfit: (
    params?: Parameters<typeof GetSalesReportItemProfit>[0],
  ) => ["salesReportItemProfit", params] as const,
  salesReportCashFlow: (
    params?: Parameters<typeof GetSalesReportCashFlow>[0],
  ) => ["salesReportCashFlow", params] as const,
  salesReportCheques: (params?: Parameters<typeof GetSalesReportCheques>[0]) =>
    ["salesReportCheques", params] as const,
  salesReportDiscounts: (
    params?: Parameters<typeof GetSalesReportDiscounts>[0],
  ) => ["salesReportDiscounts", params] as const,
  salesReportNonCashPayments: (
    params?: Parameters<typeof GetSalesReportNonCashPayments>[0],
  ) => ["salesReportNonCashPayments", params] as const,
  salesReportEmployeePerformance: (
    params?: Parameters<typeof GetSalesReportEmployeePerformance>[0],
  ) => ["salesReportEmployeePerformance", params] as const,
  inventoryReportProductStock: (
    params?: Parameters<typeof GetInventoryReportProductStock>[0],
  ) => ["inventoryReportProductStock", params] as const,
  inventoryReportExpiringItems: (
    params?: Parameters<typeof GetInventoryReportExpiringItems>[0],
  ) => ["inventoryReportExpiringItems", params] as const,
  inventoryReportPurchases: (
    params?: Parameters<typeof GetInventoryReportPurchases>[0],
  ) => ["inventoryReportPurchases", params] as const,
  inventoryReportPurchasedProducts: (
    params?: Parameters<typeof GetInventoryReportPurchasedProducts>[0],
  ) => ["inventoryReportPurchasedProducts", params] as const,
  inventoryReportReturnedItems: (
    params?: Parameters<typeof GetInventoryReportReturnedItems>[0],
  ) => ["inventoryReportReturnedItems", params] as const,
  inventoryReportSoldItems: (
    params?: Parameters<typeof GetInventoryReportSoldItems>[0],
  ) => ["inventoryReportSoldItems", params] as const,
  courierReport: (params?: Parameters<typeof GetCourierReport>[0]) =>
    ["courierReport", params] as const,
  salesReportCashOuts: (
    params?: Parameters<typeof GetSalesReportCashOuts>[0],
  ) => ["salesReportCashOuts", params] as const,
  signOffReport: (params?: Parameters<typeof GetSignOffReport>[0]) =>
    ["signOffReport", params] as const,
  salesReportByCashier: (
    params?: Parameters<typeof GetSalesReportByCashier>[0],
  ) => ["salesReportByCashier", params] as const,
  inventoryReportTotalItems: (
    params?: Parameters<typeof GetInventoryReportTotalItems>[0],
  ) => ["inventoryReportTotalItems", params] as const,
  inventoryReportProducts: (
    params?: Parameters<typeof GetInventoryReportProducts>[0],
  ) => ["inventoryReportProducts", params] as const,
  purchaseReportReturns: (
    params?: Parameters<typeof GetPurchaseReportReturns>[0],
  ) => ["purchaseReportReturns", params] as const,
  purchaseReportPaymentHistory: (
    params?: Parameters<typeof GetPurchaseReportPaymentHistory>[0],
  ) => ["purchaseReportPaymentHistory", params] as const,
  expenseReportBySellerCategory: (
    params?: Parameters<typeof GetExpenseReportBySellerCategory>[0],
  ) => ["expenseReportBySellerCategory", params] as const,
  creditReportDebtors: (
    params?: Parameters<typeof GetCreditReportDebtors>[0],
  ) => ["creditReportDebtors", params] as const,
  creditReportCreditors: (
    params?: Parameters<typeof GetCreditReportCreditors>[0],
  ) => ["creditReportCreditors", params] as const,
  creditReportCustomers: (
    params?: Parameters<typeof GetCreditReportCustomers>[0],
  ) => ["creditReportCustomers", params] as const,
  creditReportSuppliers: (
    params?: Parameters<typeof GetCreditReportSuppliers>[0],
  ) => ["creditReportSuppliers", params] as const,
  otherReportRawStockIssue: (
    params?: Parameters<typeof GetOtherReportRawStockIssue>[0],
  ) => ["otherReportRawStockIssue", params] as const,
  otherReportRawMaterialPurchases: (
    params?: Parameters<typeof GetOtherReportRawMaterialPurchases>[0],
  ) => ["otherReportRawMaterialPurchases", params] as const,
  otherReportProductions: (
    params?: Parameters<typeof GetOtherReportProductions>[0],
  ) => ["otherReportProductions", params] as const,
  otherReportTransfers: (
    params?: Parameters<typeof GetOtherReportTransfers>[0],
  ) => ["otherReportTransfers", params] as const,
};

export const invalidateReportQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["salesReportSalesLedger"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportItemProfit"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportCashFlow"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportCheques"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportDiscounts"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportNonCashPayments"] });
  queryClient.invalidateQueries({
    queryKey: ["salesReportEmployeePerformance"],
  });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportProductStock"] });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportExpiringItems"] });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportPurchases"] });
  queryClient.invalidateQueries({
    queryKey: ["inventoryReportPurchasedProducts"],
  });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportReturnedItems"] });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportSoldItems"] });
  queryClient.invalidateQueries({ queryKey: ["courierReport"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportCashOuts"] });
  queryClient.invalidateQueries({ queryKey: ["signOffReport"] });
  queryClient.invalidateQueries({ queryKey: ["salesReportByCashier"] });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportTotalItems"] });
  queryClient.invalidateQueries({ queryKey: ["inventoryReportProducts"] });
  queryClient.invalidateQueries({ queryKey: ["purchaseReportReturns"] });
  queryClient.invalidateQueries({
    queryKey: ["purchaseReportPaymentHistory"],
  });
  queryClient.invalidateQueries({
    queryKey: ["expenseReportBySellerCategory"],
  });
  queryClient.invalidateQueries({ queryKey: ["creditReportDebtors"] });
  queryClient.invalidateQueries({ queryKey: ["creditReportCreditors"] });
  queryClient.invalidateQueries({ queryKey: ["creditReportCustomers"] });
  queryClient.invalidateQueries({ queryKey: ["creditReportSuppliers"] });
  queryClient.invalidateQueries({ queryKey: ["otherReportRawStockIssue"] });
  queryClient.invalidateQueries({
    queryKey: ["otherReportRawMaterialPurchases"],
  });
  queryClient.invalidateQueries({ queryKey: ["otherReportProductions"] });
  queryClient.invalidateQueries({ queryKey: ["otherReportTransfers"] });
  queryClient.invalidateQueries({ queryKey: ["payrollSummaryReport"] });
};

export const usePayrollSummaryReportQuery = (
  params: Parameters<typeof GetPayrollSummaryReport>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.payrollSummaryReport(params),
    queryFn: async () =>
      unwrapApiResponse(await GetPayrollSummaryReport(params)),
  });
};

export const useSalesReportSalesLedgerQuery = (
  params: Parameters<typeof GetSalesReportSalesLedger>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportSalesLedger(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportSalesLedger(params)),
  });
};

export const useSalesReportSalesLedgerRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportSalesLedger>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportSalesLedger(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportSalesLedger(params)),
      }),
  });
};

export const useSalesReportItemProfitQuery = (
  params: Parameters<typeof GetSalesReportItemProfit>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportItemProfit(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportItemProfit(params)),
  });
};

export const useSalesReportItemProfitRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportItemProfit>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportItemProfit(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportItemProfit(params)),
      }),
  });
};

export const useSalesReportCashFlowQuery = (
  params: Parameters<typeof GetSalesReportCashFlow>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportCashFlow(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportCashFlow(params)),
  });
};

export const useSalesReportCashFlowRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportCashFlow>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportCashFlow(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportCashFlow(params)),
      }),
  });
};

export const useSalesReportChequesQuery = (
  params: Parameters<typeof GetSalesReportCheques>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportCheques(params),
    queryFn: async () => unwrapApiResponse(await GetSalesReportCheques(params)),
  });
};

export const useSalesReportChequesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportCheques>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportCheques(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportCheques(params)),
      }),
  });
};

export const useSalesReportDiscountsQuery = (
  params: Parameters<typeof GetSalesReportDiscounts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportDiscounts(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportDiscounts(params)),
  });
};

export const useSalesReportDiscountsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportDiscounts>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportDiscounts(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportDiscounts(params)),
      }),
  });
};

export const useSalesReportNonCashPaymentsQuery = (
  params: Parameters<typeof GetSalesReportNonCashPayments>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportNonCashPayments(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportNonCashPayments(params)),
  });
};

export const useSalesReportNonCashPaymentsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportNonCashPayments>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportNonCashPayments(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportNonCashPayments(params)),
      }),
  });
};

export const useSalesReportEmployeePerformanceQuery = (
  params: Parameters<typeof GetSalesReportEmployeePerformance>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportEmployeePerformance(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportEmployeePerformance(params)),
  });
};

export const useSalesReportEmployeePerformanceRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetSalesReportEmployeePerformance>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportEmployeePerformance(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportEmployeePerformance(params)),
      }),
  });
};

export const useInventoryReportProductStockQuery = (
  params: Parameters<typeof GetInventoryReportProductStock>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportProductStock(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportProductStock(params)),
  });
};

export const useInventoryReportProductStockRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetInventoryReportProductStock>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportProductStock(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportProductStock(params)),
      }),
  });
};

export const useInventoryReportExpiringItemsQuery = (
  params: Parameters<typeof GetInventoryReportExpiringItems>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportExpiringItems(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportExpiringItems(params)),
  });
};

export const useInventoryReportExpiringItemsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetInventoryReportExpiringItems>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportExpiringItems(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportExpiringItems(params)),
      }),
  });
};

export const useInventoryReportPurchasesQuery = (
  params: Parameters<typeof GetInventoryReportPurchases>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportPurchases(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportPurchases(params)),
  });
};

export const useInventoryReportPurchasesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetInventoryReportPurchases>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportPurchases(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportPurchases(params)),
      }),
  });
};

export const useInventoryReportPurchasedProductsQuery = (
  params: Parameters<typeof GetInventoryReportPurchasedProducts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportPurchasedProducts(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportPurchasedProducts(params)),
  });
};

export const useInventoryReportPurchasedProductsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetInventoryReportPurchasedProducts>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportPurchasedProducts(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportPurchasedProducts(params)),
      }),
  });
};

export const useInventoryReportReturnedItemsQuery = (
  params: Parameters<typeof GetInventoryReportReturnedItems>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportReturnedItems(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportReturnedItems(params)),
  });
};

export const useInventoryReportReturnedItemsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetInventoryReportReturnedItems>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportReturnedItems(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportReturnedItems(params)),
      }),
  });
};

export const useInventoryReportSoldItemsQuery = (
  params: Parameters<typeof GetInventoryReportSoldItems>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportSoldItems(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportSoldItems(params)),
  });
};

export const useInventoryReportSoldItemsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetInventoryReportSoldItems>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportSoldItems(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportSoldItems(params)),
      }),
  });
};

export const useCourierReportQuery = (
  params: Parameters<typeof GetCourierReport>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.courierReport(params),
    queryFn: async () => unwrapApiResponse(await GetCourierReport(params)),
  });
};

export const useCourierReportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCourierReport>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.courierReport(params),
        queryFn: async () => unwrapApiResponse(await GetCourierReport(params)),
      }),
  });
};

export const useSalesReportCashOutsQuery = (
  params: Parameters<typeof GetSalesReportCashOuts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportCashOuts(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportCashOuts(params)),
  });
};

export const useSalesReportCashOutsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportCashOuts>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportCashOuts(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportCashOuts(params)),
      }),
  });
};

export const useSignOffReportQuery = (
  params: Parameters<typeof GetSignOffReport>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.signOffReport(params),
    queryFn: async () => unwrapApiResponse(await GetSignOffReport(params)),
  });
};

export const useSignOffReportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSignOffReport>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.signOffReport(params),
        queryFn: async () => unwrapApiResponse(await GetSignOffReport(params)),
      }),
  });
};

export const useSalesReportByCashierQuery = (
  params: Parameters<typeof GetSalesReportByCashier>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.salesReportByCashier(params),
    queryFn: async () =>
      unwrapApiResponse(await GetSalesReportByCashier(params)),
  });
};

export const useSalesReportByCashierRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetSalesReportByCashier>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.salesReportByCashier(params),
        queryFn: async () =>
          unwrapApiResponse(await GetSalesReportByCashier(params)),
      }),
  });
};

export const useInventoryReportTotalItemsQuery = (
  params: Parameters<typeof GetInventoryReportTotalItems>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportTotalItems(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportTotalItems(params)),
  });
};

export const useInventoryReportTotalItemsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetInventoryReportTotalItems>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportTotalItems(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportTotalItems(params)),
      }),
  });
};

export const useInventoryReportProductsQuery = (
  params: Parameters<typeof GetInventoryReportProducts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.inventoryReportProducts(params),
    queryFn: async () =>
      unwrapApiResponse(await GetInventoryReportProducts(params)),
  });
};

export const useInventoryReportProductsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetInventoryReportProducts>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.inventoryReportProducts(params),
        queryFn: async () =>
          unwrapApiResponse(await GetInventoryReportProducts(params)),
      }),
  });
};

export const usePurchaseReportReturnsQuery = (
  params: Parameters<typeof GetPurchaseReportReturns>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.purchaseReportReturns(params),
    queryFn: async () =>
      unwrapApiResponse(await GetPurchaseReportReturns(params)),
  });
};

export const usePurchaseReportReturnsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetPurchaseReportReturns>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.purchaseReportReturns(params),
        queryFn: async () =>
          unwrapApiResponse(await GetPurchaseReportReturns(params)),
      }),
  });
};

export const usePurchaseReportPaymentHistoryQuery = (
  params: Parameters<typeof GetPurchaseReportPaymentHistory>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.purchaseReportPaymentHistory(params),
    queryFn: async () =>
      unwrapApiResponse(await GetPurchaseReportPaymentHistory(params)),
  });
};

export const usePurchaseReportPaymentHistoryRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetPurchaseReportPaymentHistory>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.purchaseReportPaymentHistory(params),
        queryFn: async () =>
          unwrapApiResponse(await GetPurchaseReportPaymentHistory(params)),
      }),
  });
};

export const useExpenseReportBySellerCategoryQuery = (
  params: Parameters<typeof GetExpenseReportBySellerCategory>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.expenseReportBySellerCategory(params),
    queryFn: async () =>
      unwrapApiResponse(await GetExpenseReportBySellerCategory(params)),
  });
};

export const useExpenseReportBySellerCategoryRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetExpenseReportBySellerCategory>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.expenseReportBySellerCategory(params),
        queryFn: async () =>
          unwrapApiResponse(await GetExpenseReportBySellerCategory(params)),
      }),
  });
};

export const useCreditReportDebtorsQuery = (
  params: Parameters<typeof GetCreditReportDebtors>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.creditReportDebtors(params),
    queryFn: async () =>
      unwrapApiResponse(await GetCreditReportDebtors(params)),
  });
};

export const useCreditReportDebtorsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCreditReportDebtors>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.creditReportDebtors(params),
        queryFn: async () =>
          unwrapApiResponse(await GetCreditReportDebtors(params)),
      }),
  });
};

export const useCreditReportCreditorsQuery = (
  params: Parameters<typeof GetCreditReportCreditors>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.creditReportCreditors(params),
    queryFn: async () =>
      unwrapApiResponse(await GetCreditReportCreditors(params)),
  });
};

export const useCreditReportCreditorsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCreditReportCreditors>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.creditReportCreditors(params),
        queryFn: async () =>
          unwrapApiResponse(await GetCreditReportCreditors(params)),
      }),
  });
};

export const useCreditReportCustomersQuery = (
  params: Parameters<typeof GetCreditReportCustomers>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.creditReportCustomers(params),
    queryFn: async () =>
      unwrapApiResponse(await GetCreditReportCustomers(params)),
  });
};

export const useCreditReportCustomersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCreditReportCustomers>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.creditReportCustomers(params),
        queryFn: async () =>
          unwrapApiResponse(await GetCreditReportCustomers(params)),
      }),
  });
};

export const useCreditReportSuppliersQuery = (
  params: Parameters<typeof GetCreditReportSuppliers>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.creditReportSuppliers(params),
    queryFn: async () =>
      unwrapApiResponse(await GetCreditReportSuppliers(params)),
  });
};

export const useCreditReportSuppliersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetCreditReportSuppliers>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.creditReportSuppliers(params),
        queryFn: async () =>
          unwrapApiResponse(await GetCreditReportSuppliers(params)),
      }),
  });
};

export const useOtherReportRawStockIssueQuery = (
  params: Parameters<typeof GetOtherReportRawStockIssue>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.otherReportRawStockIssue(params),
    queryFn: async () =>
      unwrapApiResponse(await GetOtherReportRawStockIssue(params)),
  });
};

export const useOtherReportRawStockIssueRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOtherReportRawStockIssue>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.otherReportRawStockIssue(params),
        queryFn: async () =>
          unwrapApiResponse(await GetOtherReportRawStockIssue(params)),
      }),
  });
};

export const useOtherReportRawMaterialPurchasesQuery = (
  params: Parameters<typeof GetOtherReportRawMaterialPurchases>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.otherReportRawMaterialPurchases(params),
    queryFn: async () =>
      unwrapApiResponse(await GetOtherReportRawMaterialPurchases(params)),
  });
};

export const useOtherReportRawMaterialPurchasesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: Parameters<typeof GetOtherReportRawMaterialPurchases>[0],
    ) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.otherReportRawMaterialPurchases(params),
        queryFn: async () =>
          unwrapApiResponse(await GetOtherReportRawMaterialPurchases(params)),
      }),
  });
};

export const useOtherReportProductionsQuery = (
  params: Parameters<typeof GetOtherReportProductions>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.otherReportProductions(params),
    queryFn: async () =>
      unwrapApiResponse(await GetOtherReportProductions(params)),
  });
};

export const useOtherReportProductionsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOtherReportProductions>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.otherReportProductions(params),
        queryFn: async () =>
          unwrapApiResponse(await GetOtherReportProductions(params)),
      }),
  });
};

export const useOtherReportTransfersQuery = (
  params: Parameters<typeof GetOtherReportTransfers>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: reportQueryKeys.otherReportTransfers(params),
    queryFn: async () =>
      unwrapApiResponse(await GetOtherReportTransfers(params)),
  });
};

export const useOtherReportTransfersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOtherReportTransfers>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: reportQueryKeys.otherReportTransfers(params),
        queryFn: async () =>
          unwrapApiResponse(await GetOtherReportTransfers(params)),
      }),
  });
};

export const useReportService = (config?: {
  fetchSalesReportSalesLedgerParams?: Parameters<
    typeof GetSalesReportSalesLedger
  >[0];
  fetchSalesReportSalesLedgerEnabled?: boolean;
  fetchSalesReportItemProfitParams?: Parameters<
    typeof GetSalesReportItemProfit
  >[0];
  fetchSalesReportItemProfitEnabled?: boolean;
  fetchSalesReportCashFlowParams?: Parameters<typeof GetSalesReportCashFlow>[0];
  fetchSalesReportCashFlowEnabled?: boolean;
  fetchSalesReportChequesParams?: Parameters<typeof GetSalesReportCheques>[0];
  fetchSalesReportChequesEnabled?: boolean;
  fetchSalesReportDiscountsParams?: Parameters<
    typeof GetSalesReportDiscounts
  >[0];
  fetchSalesReportDiscountsEnabled?: boolean;
  fetchSalesReportNonCashPaymentsParams?: Parameters<
    typeof GetSalesReportNonCashPayments
  >[0];
  fetchSalesReportNonCashPaymentsEnabled?: boolean;
  fetchSalesReportEmployeePerformanceParams?: Parameters<
    typeof GetSalesReportEmployeePerformance
  >[0];
  fetchSalesReportEmployeePerformanceEnabled?: boolean;
  fetchInventoryReportProductStockParams?: Parameters<
    typeof GetInventoryReportProductStock
  >[0];
  fetchInventoryReportProductStockEnabled?: boolean;
  fetchInventoryReportExpiringItemsParams?: Parameters<
    typeof GetInventoryReportExpiringItems
  >[0];
  fetchInventoryReportExpiringItemsEnabled?: boolean;
  fetchInventoryReportPurchasesParams?: Parameters<
    typeof GetInventoryReportPurchases
  >[0];
  fetchInventoryReportPurchasesEnabled?: boolean;
  fetchInventoryReportPurchasedProductsParams?: Parameters<
    typeof GetInventoryReportPurchasedProducts
  >[0];
  fetchInventoryReportPurchasedProductsEnabled?: boolean;
  fetchInventoryReportReturnedItemsParams?: Parameters<
    typeof GetInventoryReportReturnedItems
  >[0];
  fetchInventoryReportReturnedItemsEnabled?: boolean;
  fetchInventoryReportSoldItemsParams?: Parameters<
    typeof GetInventoryReportSoldItems
  >[0];
  fetchInventoryReportSoldItemsEnabled?: boolean;
  fetchSalesReportCashOutsParams?: Parameters<typeof GetSalesReportCashOuts>[0];
  fetchSalesReportCashOutsEnabled?: boolean;
  fetchSignOffReportParams?: Parameters<typeof GetSignOffReport>[0];
  fetchSignOffReportEnabled?: boolean;
  fetchSalesReportByCashierParams?: Parameters<
    typeof GetSalesReportByCashier
  >[0];
  fetchSalesReportByCashierEnabled?: boolean;
  fetchInventoryReportTotalItemsParams?: Parameters<
    typeof GetInventoryReportTotalItems
  >[0];
  fetchInventoryReportTotalItemsEnabled?: boolean;
  fetchInventoryReportProductsParams?: Parameters<
    typeof GetInventoryReportProducts
  >[0];
  fetchInventoryReportProductsEnabled?: boolean;
  fetchPurchaseReportReturnsParams?: Parameters<
    typeof GetPurchaseReportReturns
  >[0];
  fetchPurchaseReportReturnsEnabled?: boolean;
  fetchPurchaseReportPaymentHistoryParams?: Parameters<
    typeof GetPurchaseReportPaymentHistory
  >[0];
  fetchPurchaseReportPaymentHistoryEnabled?: boolean;
  fetchExpenseReportBySellerCategoryParams?: Parameters<
    typeof GetExpenseReportBySellerCategory
  >[0];
  fetchExpenseReportBySellerCategoryEnabled?: boolean;
  fetchCreditReportDebtorsParams?: Parameters<typeof GetCreditReportDebtors>[0];
  fetchCreditReportDebtorsEnabled?: boolean;
  fetchCreditReportCreditorsParams?: Parameters<
    typeof GetCreditReportCreditors
  >[0];
  fetchCreditReportCreditorsEnabled?: boolean;
  fetchCreditReportCustomersParams?: Parameters<
    typeof GetCreditReportCustomers
  >[0];
  fetchCreditReportCustomersEnabled?: boolean;
  fetchCreditReportSuppliersParams?: Parameters<
    typeof GetCreditReportSuppliers
  >[0];
  fetchCreditReportSuppliersEnabled?: boolean;
  fetchOtherReportRawStockIssueParams?: Parameters<
    typeof GetOtherReportRawStockIssue
  >[0];
  fetchOtherReportRawStockIssueEnabled?: boolean;
  fetchOtherReportRawMaterialPurchasesParams?: Parameters<
    typeof GetOtherReportRawMaterialPurchases
  >[0];
  fetchOtherReportRawMaterialPurchasesEnabled?: boolean;
  fetchOtherReportProductionsParams?: Parameters<
    typeof GetOtherReportProductions
  >[0];
  fetchOtherReportProductionsEnabled?: boolean;
  fetchOtherReportTransfersParams?: Parameters<
    typeof GetOtherReportTransfers
  >[0];
  fetchOtherReportTransfersEnabled?: boolean;
  fetchCourierReportParams?: Parameters<typeof GetCourierReport>[0];
  fetchCourierReportEnabled?: boolean;
}) => {
  const salesReportSalesLedgerQuery = useSalesReportSalesLedgerQuery(
    config?.fetchSalesReportSalesLedgerParams,
    {
      enabled:
        config?.fetchSalesReportSalesLedgerEnabled !== false &&
        config?.fetchSalesReportSalesLedgerParams !== undefined &&
        config?.fetchSalesReportSalesLedgerParams !== null,
    },
  );

  const salesReportItemProfitQuery = useSalesReportItemProfitQuery(
    config?.fetchSalesReportItemProfitParams,
    {
      enabled:
        config?.fetchSalesReportItemProfitEnabled !== false &&
        config?.fetchSalesReportItemProfitParams !== undefined &&
        config?.fetchSalesReportItemProfitParams !== null,
    },
  );

  const salesReportCashFlowQuery = useSalesReportCashFlowQuery(
    config?.fetchSalesReportCashFlowParams,
    {
      enabled:
        config?.fetchSalesReportCashFlowEnabled !== false &&
        config?.fetchSalesReportCashFlowParams !== undefined &&
        config?.fetchSalesReportCashFlowParams !== null,
    },
  );

  const salesReportChequesQuery = useSalesReportChequesQuery(
    config?.fetchSalesReportChequesParams,
    {
      enabled:
        config?.fetchSalesReportChequesEnabled !== false &&
        config?.fetchSalesReportChequesParams !== undefined &&
        config?.fetchSalesReportChequesParams !== null,
    },
  );

  const salesReportDiscountsQuery = useSalesReportDiscountsQuery(
    config?.fetchSalesReportDiscountsParams,
    {
      enabled:
        config?.fetchSalesReportDiscountsEnabled !== false &&
        config?.fetchSalesReportDiscountsParams !== undefined &&
        config?.fetchSalesReportDiscountsParams !== null,
    },
  );

  const salesReportNonCashPaymentsQuery = useSalesReportNonCashPaymentsQuery(
    config?.fetchSalesReportNonCashPaymentsParams,
    {
      enabled:
        config?.fetchSalesReportNonCashPaymentsEnabled !== false &&
        config?.fetchSalesReportNonCashPaymentsParams !== undefined &&
        config?.fetchSalesReportNonCashPaymentsParams !== null,
    },
  );

  const salesReportEmployeePerformanceQuery =
    useSalesReportEmployeePerformanceQuery(
      config?.fetchSalesReportEmployeePerformanceParams,
      {
        enabled:
          config?.fetchSalesReportEmployeePerformanceEnabled !== false &&
          config?.fetchSalesReportEmployeePerformanceParams !== undefined &&
          config?.fetchSalesReportEmployeePerformanceParams !== null,
      },
    );

  const inventoryReportProductStockQuery = useInventoryReportProductStockQuery(
    config?.fetchInventoryReportProductStockParams,
    {
      enabled:
        config?.fetchInventoryReportProductStockEnabled !== false &&
        config?.fetchInventoryReportProductStockParams !== undefined &&
        config?.fetchInventoryReportProductStockParams !== null,
    },
  );

  const inventoryReportExpiringItemsQuery =
    useInventoryReportExpiringItemsQuery(
      config?.fetchInventoryReportExpiringItemsParams,
      {
        enabled:
          config?.fetchInventoryReportExpiringItemsEnabled !== false &&
          config?.fetchInventoryReportExpiringItemsParams !== undefined &&
          config?.fetchInventoryReportExpiringItemsParams !== null,
      },
    );

  const inventoryReportPurchasesQuery = useInventoryReportPurchasesQuery(
    config?.fetchInventoryReportPurchasesParams,
    {
      enabled:
        config?.fetchInventoryReportPurchasesEnabled !== false &&
        config?.fetchInventoryReportPurchasesParams !== undefined &&
        config?.fetchInventoryReportPurchasesParams !== null,
    },
  );

  const inventoryReportPurchasedProductsQuery =
    useInventoryReportPurchasedProductsQuery(
      config?.fetchInventoryReportPurchasedProductsParams,
      {
        enabled:
          config?.fetchInventoryReportPurchasedProductsEnabled !== false &&
          config?.fetchInventoryReportPurchasedProductsParams !== undefined &&
          config?.fetchInventoryReportPurchasedProductsParams !== null,
      },
    );

  const inventoryReportReturnedItemsQuery =
    useInventoryReportReturnedItemsQuery(
      config?.fetchInventoryReportReturnedItemsParams,
      {
        enabled:
          config?.fetchInventoryReportReturnedItemsEnabled !== false &&
          config?.fetchInventoryReportReturnedItemsParams !== undefined &&
          config?.fetchInventoryReportReturnedItemsParams !== null,
      },
    );

  const inventoryReportSoldItemsQuery = useInventoryReportSoldItemsQuery(
    config?.fetchInventoryReportSoldItemsParams,
    {
      enabled:
        config?.fetchInventoryReportSoldItemsEnabled !== false &&
        config?.fetchInventoryReportSoldItemsParams !== undefined &&
        config?.fetchInventoryReportSoldItemsParams !== null,
    },
  );

  const salesReportCashOutsQuery = useSalesReportCashOutsQuery(
    config?.fetchSalesReportCashOutsParams,
    {
      enabled:
        config?.fetchSalesReportCashOutsEnabled !== false &&
        config?.fetchSalesReportCashOutsParams !== undefined &&
        config?.fetchSalesReportCashOutsParams !== null,
    },
  );

  const signOffReportQuery = useSignOffReportQuery(
    config?.fetchSignOffReportParams,
    {
      enabled:
        config?.fetchSignOffReportEnabled !== false &&
        config?.fetchSignOffReportParams !== undefined &&
        config?.fetchSignOffReportParams !== null,
    },
  );

  const salesReportByCashierQuery = useSalesReportByCashierQuery(
    config?.fetchSalesReportByCashierParams,
    {
      enabled:
        config?.fetchSalesReportByCashierEnabled !== false &&
        config?.fetchSalesReportByCashierParams !== undefined &&
        config?.fetchSalesReportByCashierParams !== null,
    },
  );

  const inventoryReportTotalItemsQuery = useInventoryReportTotalItemsQuery(
    config?.fetchInventoryReportTotalItemsParams,
    {
      enabled:
        config?.fetchInventoryReportTotalItemsEnabled !== false &&
        config?.fetchInventoryReportTotalItemsParams !== undefined &&
        config?.fetchInventoryReportTotalItemsParams !== null,
    },
  );

  const inventoryReportProductsQuery = useInventoryReportProductsQuery(
    config?.fetchInventoryReportProductsParams,
    {
      enabled:
        config?.fetchInventoryReportProductsEnabled !== false &&
        config?.fetchInventoryReportProductsParams !== undefined &&
        config?.fetchInventoryReportProductsParams !== null,
    },
  );

  const purchaseReportReturnsQuery = usePurchaseReportReturnsQuery(
    config?.fetchPurchaseReportReturnsParams,
    {
      enabled:
        config?.fetchPurchaseReportReturnsEnabled !== false &&
        config?.fetchPurchaseReportReturnsParams !== undefined &&
        config?.fetchPurchaseReportReturnsParams !== null,
    },
  );

  const purchaseReportPaymentHistoryQuery =
    usePurchaseReportPaymentHistoryQuery(
      config?.fetchPurchaseReportPaymentHistoryParams,
      {
        enabled:
          config?.fetchPurchaseReportPaymentHistoryEnabled !== false &&
          config?.fetchPurchaseReportPaymentHistoryParams !== undefined &&
          config?.fetchPurchaseReportPaymentHistoryParams !== null,
      },
    );

  const expenseReportBySellerCategoryQuery =
    useExpenseReportBySellerCategoryQuery(
      config?.fetchExpenseReportBySellerCategoryParams,
      {
        enabled:
          config?.fetchExpenseReportBySellerCategoryEnabled !== false &&
          config?.fetchExpenseReportBySellerCategoryParams !== undefined &&
          config?.fetchExpenseReportBySellerCategoryParams !== null,
      },
    );

  const creditReportDebtorsQuery = useCreditReportDebtorsQuery(
    config?.fetchCreditReportDebtorsParams,
    {
      enabled:
        config?.fetchCreditReportDebtorsEnabled !== false &&
        config?.fetchCreditReportDebtorsParams !== undefined &&
        config?.fetchCreditReportDebtorsParams !== null,
    },
  );

  const creditReportCreditorsQuery = useCreditReportCreditorsQuery(
    config?.fetchCreditReportCreditorsParams,
    {
      enabled:
        config?.fetchCreditReportCreditorsEnabled !== false &&
        config?.fetchCreditReportCreditorsParams !== undefined &&
        config?.fetchCreditReportCreditorsParams !== null,
    },
  );

  const creditReportCustomersQuery = useCreditReportCustomersQuery(
    config?.fetchCreditReportCustomersParams,
    {
      enabled:
        config?.fetchCreditReportCustomersEnabled !== false &&
        config?.fetchCreditReportCustomersParams !== undefined &&
        config?.fetchCreditReportCustomersParams !== null,
    },
  );

  const creditReportSuppliersQuery = useCreditReportSuppliersQuery(
    config?.fetchCreditReportSuppliersParams,
    {
      enabled:
        config?.fetchCreditReportSuppliersEnabled !== false &&
        config?.fetchCreditReportSuppliersParams !== undefined &&
        config?.fetchCreditReportSuppliersParams !== null,
    },
  );

  const otherReportRawStockIssueQuery = useOtherReportRawStockIssueQuery(
    config?.fetchOtherReportRawStockIssueParams,
    {
      enabled:
        config?.fetchOtherReportRawStockIssueEnabled !== false &&
        config?.fetchOtherReportRawStockIssueParams !== undefined &&
        config?.fetchOtherReportRawStockIssueParams !== null,
    },
  );

  const otherReportRawMaterialPurchasesQuery =
    useOtherReportRawMaterialPurchasesQuery(
      config?.fetchOtherReportRawMaterialPurchasesParams,
      {
        enabled:
          config?.fetchOtherReportRawMaterialPurchasesEnabled !== false &&
          config?.fetchOtherReportRawMaterialPurchasesParams !== undefined &&
          config?.fetchOtherReportRawMaterialPurchasesParams !== null,
      },
    );

  const otherReportProductionsQuery = useOtherReportProductionsQuery(
    config?.fetchOtherReportProductionsParams,
    {
      enabled:
        config?.fetchOtherReportProductionsEnabled !== false &&
        config?.fetchOtherReportProductionsParams !== undefined &&
        config?.fetchOtherReportProductionsParams !== null,
    },
  );

  const otherReportTransfersQuery = useOtherReportTransfersQuery(
    config?.fetchOtherReportTransfersParams,
    {
      enabled:
        config?.fetchOtherReportTransfersEnabled !== false &&
        config?.fetchOtherReportTransfersParams !== undefined &&
        config?.fetchOtherReportTransfersParams !== null,
    },
  );

  const courierReportQuery = useCourierReportQuery(
    config?.fetchCourierReportParams,
    {
      enabled:
        config?.fetchCourierReportEnabled !== false &&
        config?.fetchCourierReportParams !== undefined &&
        config?.fetchCourierReportParams !== null,
    },
  );

  return {
    salesReportSalesLedgerQuery,
    salesReportItemProfitQuery,
    salesReportCashFlowQuery,
    salesReportChequesQuery,
    salesReportDiscountsQuery,
    salesReportNonCashPaymentsQuery,
    salesReportEmployeePerformanceQuery,
    inventoryReportProductStockQuery,
    inventoryReportExpiringItemsQuery,
    inventoryReportPurchasesQuery,
    inventoryReportPurchasedProductsQuery,
    inventoryReportReturnedItemsQuery,
    inventoryReportSoldItemsQuery,
    salesReportCashOutsQuery,
    signOffReportQuery,
    salesReportByCashierQuery,
    inventoryReportTotalItemsQuery,
    inventoryReportProductsQuery,
    purchaseReportReturnsQuery,
    purchaseReportPaymentHistoryQuery,
    expenseReportBySellerCategoryQuery,
    creditReportDebtorsQuery,
    creditReportCreditorsQuery,
    creditReportCustomersQuery,
    creditReportSuppliersQuery,
    otherReportRawStockIssueQuery,
    otherReportRawMaterialPurchasesQuery,
    otherReportProductionsQuery,
    otherReportTransfersQuery,
    courierReportQuery,
  };
};
